// @ts-nocheck

import { gsap } from 'gsap';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';
import { map } from 'lodash';
import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { WinaTvRed } from '../../assets/svg/common';
import { Valid } from '../../assets/svg/common';
import { __ } from '../../i18n/l10n';
import { type MatchState } from '../../states/entities/matches';
import { MatchEntity } from '../../states/entities/matches';
import theme from '../../styled-components/dark';
import { MultiviewBlockWrapper } from '../../styled-components/shared';
import { type FacetType } from '../../types/facets';
import ListItem from '../ListItem';
import MatchRowsFiltersBar from '../MatchRowsFiltersBar';

gsap.registerPlugin(ScrollToPlugin);

const Wrapper = styled(MultiviewBlockWrapper)`
  border: 0;
  display: flex;
  flex-direction: column;
  margin: 0 0 2em 0;
`;

const Title = styled.div`
  height: 3em;
  background-color: ${theme.Modal.header.backgroundColor};
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: ${theme.Modal.header.color};
  text-transform: uppercase;
  font-size: 1.25em;
  font-weight: 600;
  margin-bottom: 1em;
`;

const BorderRadiusWrapper = styled.div`
  ${({ $isVisible }) => {
    if ($isVisible) {
      return css`
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 2em;
      `;
    }
  }}
`;

const CategoryTournamentButton = styled.div`
  background-color: transparent;
  color: ${theme.Modal.categoryTitle.color};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1em;
  margin-left: 0.6em;
  text-transform: uppercase;
  font-size: 1.125em;
  font-weight: 600;
  &&& {
    svg {
      width: 1em;
      height: 1em;
      margin-left: 0.3125em;
      use {
        fill: #808080;
      }
    }
  }
`;

const StyledMatchRowsFiltersBar = styled(MatchRowsFiltersBar)`
  margin: 0 0 1em 0;
  border-radius: 1em;
`;

const MatchTitle = styled.div`
  text-align: left;
  align-items: center;
  display: flex;
  color: ${theme.Modal.line.color};
`;

const MatchWTVLogo = styled(WinaTvRed)`
  width: 1.875em;
  height: 1.2em;
`;

const ContentWrapper = styled.div`
  margin: 0 1em;
  display: flex;
  flex-direction: column;
`;

const Empty = styled.div`
  display: block;
  text-align: center;
  padding: 1em;
  color: ${theme.Modal.line.color};
`;

const StyledValidIcon = styled(Valid)`
  color: ${theme.Modal.line.validIconColor};
  width: 1.3em;
  height: 1.3em;
  padding-right: 0.5em;
`;

const StyledListItem = styled(ListItem)`
  padding: 0.8em 0.5em;
  justify-content: space-between;
`;

const CategoryTournamentWrapper = styled.div``;

type MultiviewModalBlockPickerProps = {
  className?: string;
  addMatch: (matchId: number) => void;
  delMatch: (matchId: number) => void;
  facets: Array<FacetType>;
  isWTVEnabled: boolean;
  multiviewList: Array<MatchState>;
  scrollToHeader: boolean;
  selectedSportId?: number | null;
  setScrollToHeaderOnTheNextShowMatchList: (scrollToHeader: boolean) => void;
  setSelectedSportId: (sportId: number) => void;
  setShowWinamaxTv: (showWTV: boolean) => void;
  showWTV: boolean;
  groups: Array<unknown>;
};

const MultiviewModalBlockPicker = ({
  className,
  addMatch,
  delMatch,
  facets,
  isWTVEnabled,
  multiviewList,
  scrollToHeader,
  selectedSportId = null,
  setScrollToHeaderOnTheNextShowMatchList,
  setSelectedSportId,
  setShowWinamaxTv,
  showWTV,
  groups,
}: MultiviewModalBlockPickerProps) => {
  const tweenRef = useRef(null);

  useEffect(() => {
    if (scrollToHeader) {
      let tween = tweenRef.current;
      const header = document.getElementById('masthead');
      if (header && window.scrollY < header.offsetTop && tween) {
        tween = gsap.to(window, {
          duration: 0.5,
          scrollTo: header.offsetTop,
        });
      }
      setScrollToHeaderOnTheNextShowMatchList(false);
    }
  }, [scrollToHeader, setScrollToHeaderOnTheNextShowMatchList]);

  const onSelectSportId = (sportId) => {
    if (sportId === selectedSportId) {
      setSelectedSportId(null);
    } else {
      setSelectedSportId(sportId);
    }
  };

  const onClickMatchButton = (matchId) => () =>
    multiviewList.indexOf(matchId) !== -1 ? delMatch(matchId) : addMatch(matchId);

  const onClickWTVButton = () => setShowWinamaxTv(!showWTV);

  return (
    <Wrapper>
      <Title>{__('events in progress')}</Title>
      <ContentWrapper>
        <StyledMatchRowsFiltersBar facets={facets} selectedSportIds={[selectedSportId]} onClickItem={onSelectSportId} />
        {!groups && !isWTVEnabled ? <Empty>{__('no events')}</Empty> : null}
        {groups || isWTVEnabled ? (
          <React.Fragment>
            {isWTVEnabled ? (
              <BorderRadiusWrapper $isVisible>
                <StyledListItem onClick={onClickWTVButton}>
                  <MatchTitle>
                    {showWTV ? <StyledValidIcon /> : null}
                    Winamax TV
                  </MatchTitle>
                  <MatchWTVLogo />
                </StyledListItem>
              </BorderRadiusWrapper>
            ) : null}
            {groups
              ? map(groups, (group) => {
                  return (
                    <React.Fragment key={group.matchId}>
                      <CategoryTournamentButton>
                        <CategoryTournamentWrapper>{group.name}</CategoryTournamentWrapper>
                      </CategoryTournamentButton>
                      <BorderRadiusWrapper key={group.matchId} $isVisible>
                        {map(group.items, (match, idx) => {
                          return (
                            <StyledListItem
                              key={match.matchId}
                              className={className}
                              onClick={onClickMatchButton(match.matchId)}
                              depth={1}
                              odd={idx % 2 === 1}
                            >
                              <MatchTitle>
                                {multiviewList.indexOf(match.matchId) !== -1 ? <StyledValidIcon /> : null}

                                {match.title}
                              </MatchTitle>
                              {MatchEntity.isWinamaxTvChannel(match) ? <MatchWTVLogo /> : null}
                            </StyledListItem>
                          );
                        })}
                      </BorderRadiusWrapper>
                    </React.Fragment>
                  );
                })
              : null}
          </React.Fragment>
        ) : null}
      </ContentWrapper>
    </Wrapper>
  );
};

export default MultiviewModalBlockPicker;
